import { useL10n } from "@ews/react-localization-context";
import { IonContent, IonInput, IonLabel, IonSegmentButton } from "@ionic/react";
import React, { FC, useEffect, useRef, useState } from "react";
import { SwiperSlide, useSwiper } from "swiper/react";
import { useHasLicense } from "../../../HasLicense";
import Overflow from "../../../Overflow/Overflow";
import { useIsMobile } from "../../../Responsive";
import SettingsSwiper from "../../../Settings/SettingsComponents/SettingsSwiper/SettingsSwiper";
import SettingsGrid from "../../../Settings/SettingsGrid/SettingsGrid";
import SettingsSegment from "../../../Settings/SettingsSegment/SettingsSegment";
import DetailsSettings from "../DetailsSettings/DetailsSettings";
import GPSSnapshot from "../GPSSnapshot/GPSSnapshot";
import LicenseSettings from "../LicenseSettings/LicenseSettings";
import MainSettings from "../MainSettings/MainSettings";
import MaintenanceReportsSettings from "../MaintenanceReportsSettings/MaintenanceReportsSettings";
import NotificationsSettings from "../NotificationsSettings/NotificationsSettings";
import PermissionsSettings from "../PermissionsSettings/PermissionsSettings";
import PlanView from "../PlanView/PlanView";
import { DisplayTabProps, SettingsOverlayProps } from "../types";
import style from './SettingsOverlay.module.scss';
import { LicenseType, permissionAllowed } from "@ews/react-data";
import { useMyProfileId } from "../../../Login/LoginProvider";
import { useUserPermissions } from "../../../ReactData/user";


export const DisplayTab: React.FC<DisplayTabProps> = ({ name, display, children }) =>
{
    if (name === display) {
        return <>{children}</>;
    }
    return <></>;
};

const SettingsOverlay: React.FC<SettingsOverlayProps> = ({ systemId, onClose }) =>
{


    type Page = {
        value: string;
        title: string;
        child: FC<{ systemId: string; }>;
        edit: boolean;
    };

    const me = useMyProfileId();
    const myPermissions = useUserPermissions(me);
    const allowedToEdit = permissionAllowed(myPermissions.grantSystemAccess || 0);

    const hasPlanViewLicense = useHasLicense(LicenseType.PLAN_VIEW);

    const pageList: Page[] = [
        {
            value: 'general',
            title: "TD_ALLGEMEIN",
            child: MainSettings,
            edit: allowedToEdit
        },
        {
            value: 'details',
            title: "TD_DETAILS2",
            child: DetailsSettings,
            edit: allowedToEdit
        },
        {
            value: 'rights',
            title: "COM_FACPMANAGEMENT_CUSTOMER_ACCOUNT_PERMISSIONS",
            child: PermissionsSettings,
            edit: allowedToEdit
        },
        {
            value: 'license',
            title: "COM_FACPMANAGEMENT_USER_ACCOUNT_LICENCE",
            child: LicenseSettings,
            edit: true
        },
        {
            value: 'notifications',
            title: "COM_FACPMANAGEMENT_USER_ACCOUNT_PERMISSIONS_EVENTS",
            child: NotificationsSettings,
            edit: allowedToEdit
        },
        {
            value: 'gpsdetails',
            title: "[REACT_GPS_DETAILS]",
            child: GPSSnapshot,
            edit: allowedToEdit
        },
        {
            value: 'maintenance reports',
            title: "COM_FACPMANAGEMENT_USER_ACCOUNT_MAINTENANCE",
            child: MaintenanceReportsSettings,
            edit: allowedToEdit
        },
        {
            value: 'plan view',
            title: "COM_FACPMANAGEMENT_FACP_ELS_PLANANSICHT",
            child: PlanView,
            edit: allowedToEdit && hasPlanViewLicense
        },
    ];
    const mobileView = useIsMobile();
    const SliderComponent = mobileView ? SwiperSlide : ({ children }: { children: React.ReactNode; }) => <>{children}</>;


    const [currentSegment, setCurrentSegment] = useState<string>(pageList[0].value);
    const slideToRef = useRef<number>(0);
    const SwiperRef = useRef<any>();

    const { translate: t } = useL10n();

    const SwiperButtonNext: React.FC<{ swipeRef: any; }> = ({ swipeRef }) =>
    {
        const swiper = useSwiper();
        return <IonInput
            label=""
            ref={swipeRef}
            onClick={() => swiper.slideTo(slideToRef.current)}
            hidden />;
    };

    useEffect(() =>
    {
        if (!myPermissions.notReadyFlag && !allowedToEdit) {
            const firstPageView = pageList.filter(page => page.edit === true)[0].value;
            setCurrentSegment(firstPageView);
        }
    }, [myPermissions]);

    return <>
        <IonContent id="settingsContent" className={style.overlaySettings} fullscreen>

            <SettingsSegment value={currentSegment} onSelect={(current: string) => setCurrentSegment(current)}>
                {pageList.map((segment, index) => (

                    <IonSegmentButton key={segment.value} disabled={!segment.edit} value={segment.value} onClick={() =>
                    {
                        if (mobileView) {
                            slideToRef.current = index;
                            SwiperRef.current.click();
                        }
                    }}>
                        <IonLabel>{t(segment.title)}</IonLabel>
                    </IonSegmentButton>

                ))}
            </SettingsSegment>

            <Overflow minHeight={"calc(100% - 56px"} maxHeight={"calc(100% - 56px"}>
                <SettingsSwiper

                    onSlideChange={(index) =>
                    {
                        slideToRef.current = index;
                        setCurrentSegment(pageList[index].value);
                    }}>
                    {mobileView && <SwiperButtonNext swipeRef={SwiperRef} />}
                    {pageList.map((page, index) =>
                    {
                        const { child, value } = page;
                        const Component = child;
                        return page.edit ? <SliderComponent key={index}>
                            <DisplayTab name={value} display={currentSegment}>

                                <SettingsGrid>
                                    <Component systemId={systemId} />
                                </SettingsGrid>

                            </DisplayTab>
                        </SliderComponent> : null;
                    })}
                </SettingsSwiper>
            </Overflow>
        </IonContent >
    </>;

};

export default SettingsOverlay;
